import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import type {
  GetUserPokerGamesParams,
  PokerGamesResponse,
} from "../../types/poker";

type StoredToken = { AccessToken?: string };

const DEFAULT_RESPONSE: PokerGamesResponse = {
  data: [],
  pagination: {
    current_page: 1,
    total_pages: null,
    total_games: null,
    per_page: 20,
    has_next: false,
    has_previous: false,
    next_cursor: null,
  },
};

const fetchUserPokerGames = async (
  params: GetUserPokerGamesParams,
): Promise<PokerGamesResponse> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);

  const body: Record<string, unknown> = {
    user_id: params.user_id,
    page: params.page,
    limit: params.limit ?? 20,
    start_date: params.start_date ?? null,
    end_date: params.end_date ?? null,
  };

  // Mirrors the legacy behaviour: when searching by game_id the backend
  // ignores money_type, so we drop it from the payload.
  if (params.game_id) {
    body.game_id = params.game_id;
  } else if (params.money_type) {
    body.money_type = params.money_type;
  }

  if (params.cursor) {
    body.cursor = params.cursor;
  }

  const url = API_ROUTES.GET_USER_POKER_GAMES.replace(
    "{user_id}",
    params.user_id,
  );

  const response = await instance.post(url, body);
  const responseBody = response?.data?.data;
  if (!responseBody || typeof responseBody !== "object") {
    return DEFAULT_RESPONSE;
  }
  return responseBody as PokerGamesResponse;
};

export const useGetUserPokerGames = (
  params: GetUserPokerGamesParams | null,
): UseQueryResult<PokerGamesResponse, Error> => {
  return useQuery<PokerGamesResponse, Error>({
    queryKey: [
      "GET_USER_POKER_GAMES",
      params?.user_id ?? "",
      params?.page ?? 1,
      params?.limit ?? 20,
      params?.start_date ?? "",
      params?.end_date ?? "",
      params?.money_type ?? "",
      params?.cursor ?? "",
      params?.game_id ?? "",
    ],
    queryFn: () => fetchUserPokerGames(params as GetUserPokerGamesParams),
    enabled: Boolean(params?.user_id),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
